import { motion } from 'framer-motion';
import { ClubCard } from '../components/ClubCard';
import {
  Award,
  CalendarClock,
  Coins,
  Crown,
  Gem,
  Medal,
  ShieldCheck,
  Star,
  Trophy,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { PageHeading, ProgressBar, StatCard, StatusPill } from '../components/Ui';
import { formatDate, formatToken } from '../lib/format';
import { atomicToDecimal, useAppData } from '../state/AppDataContext';

/*
 * Leaders Club page (route /royal, rule 13). Owner 2026-10-11: only what the BDynamicLeadersClub contract did is shown -
 * the four clubs (Club 4 / 8 / 12 / 16) from /api/public/rank-pools (ClubEntered members, ClubPaid totals, the
 * contract's next settlement time) and the member's own club standing. There is no rank-pool or NFT-pool contract,
 * so no rank, epoch or NFT figure is shown.
 */

function useCountdown(target: number | null) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  return useMemo(() => {
    let seconds = target === null ? 0 : Math.max(0, Math.floor((target - now) / 1_000));
    const days = Math.floor(seconds / 86_400); seconds %= 86_400;
    const hours = Math.floor(seconds / 3_600); seconds %= 3_600;
    const minutes = Math.floor(seconds / 60); seconds %= 60;
    return [days, hours, minutes, seconds].map((value) => value.toString().padStart(2, '0'));
  }, [target, now]);
}

/** "0.0001" (18-decimal BTCB text) -> atomic units, for an exact sum of the per-club ClubPaid totals. */
function decimalToAtomic(value: string): bigint {
  const [whole = '0', fraction = ''] = value.split('.');
  return /^\d+$/.test(whole) && /^\d*$/.test(fraction) ? BigInt(whole + fraction.padEnd(18, '0').slice(0, 18)) : 0n;
}

export function RoyalPage() {
  const data = useAppData();
  const clubs = data.ranks;
  const nextSettlement = useMemo(() => {
    const target = Date.parse(clubs[0]?.nextDistributionAt ?? '');
    return Number.isFinite(target) ? target : null;
  }, [clubs]);
  const countdown = useCountdown(nextSettlement);
  const members = clubs.reduce((total, club) => total + club.current, 0);
  const paidAtomic = clubs.reduce((total, club) => total + decimalToAtomic(club.distributedAmount), 0n);
  return (
    <>
      <PageHeading
        eyebrow="LEADERS CLUB · ON-CHAIN"
        title="Leaders Club"
        description="The four clubs (Club 4, 8, 12, 16) exactly as the Leaders Club contract records them: members, payouts and the next settlement."
        actions={<StatusPill tone="green"><ShieldCheck size={12} /> On-chain · Leaders Club contract</StatusPill>}
      />
      {data.club ? <ClubCard club={data.club} eyebrow="YOUR LEADERS CLUB" title="Club standing & income" /> : null}
      <section className="royal-hero panel">
        <div className="royal-copy"><span className="eyebrow"><CalendarClock size={13} /> NEXT SETTLEMENT (CONTRACT)</span><h2>Club income is paid <em>by the contract.</em></h2><p>Each settlement splits the vault&apos;s LEADERS_CLUB fund 25% per club, shared equally by that club&apos;s eligible IDs (2+ directs). The countdown is the contract&apos;s own next settlement time.</p>
          {nextSettlement !== null ? (
            <div className="epoch-countdown" aria-label={`${countdown[0]} days ${countdown[1]} hours ${countdown[2]} minutes ${countdown[3]} seconds until the next settlement`}>
              {countdown.map((value, index) => <span key={index}><strong>{value}</strong><small>{['DAYS', 'HRS', 'MIN', 'SEC'][index]}</small></span>)}
            </div>
          ) : <p className="empty-copy">The contract has not scheduled a settlement yet.</p>}
        </div>
        <div className="royal-crest"><Crown /><div className="crest-ring ring-one" /><div className="crest-ring ring-two" /><span>LC</span><small>CLUB</small></div>
      </section>
      <section className="stats-grid four royal-stats">
        <StatCard label="Paid by the clubs" value={formatToken(atomicToDecimal(paidAtomic.toString()))} detail="Sum of ClubPaid events" icon={Coins} tone="violet" />
        <StatCard label="Clubs" value={clubs.length.toLocaleString()} detail="Reported by the Leaders Club contract" icon={Crown} tone="pink" delay={0.05} />
        <StatCard label="Club members" value={members.toLocaleString()} detail="ClubEntered records (all clubs)" icon={Users} tone="cyan" delay={0.1} />
        <StatCard label="Next settlement" value={nextSettlement !== null ? countdown.slice(0, 3).join(':') : 'Not scheduled'} detail="Days : hours : minutes (contract time)" icon={Trophy} tone="green" delay={0.15} />
      </section>
      <section>
        <div className="section-heading"><div><span className="eyebrow">CLUBS · ON-CHAIN</span><h2>Club 4 · Club 8 · Club 12 · Club 16</h2><p>Members are the contract&apos;s ClubEntered records, paid totals its ClubPaid events, the next settlement its own schedule.</p></div><StatusPill tone="green"><ShieldCheck size={12} /> On-chain</StatusPill></div>
        <div className="rank-grid">{clubs.map((club, index) => <motion.article className={`panel rank-card rank-tone-${club.level} unlocked`} key={club.rank} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.08 }} whileHover={{ y: -5 }}><div className="rank-visual"><div className="rank-rays" /><span>{club.level === 1 ? <Star /> : club.level === 2 ? <Medal /> : club.level === 3 ? <Award /> : <Gem />}</span></div><span className="eyebrow">LEADERS CLUB</span><h3>{club.rank}</h3><p>{club.current.toLocaleString()} member{club.current === 1 ? '' : 's'} on-chain (ClubEntered).</p><div className="rank-progress"><div><span>Share of each settlement fund</span><strong>{club.share}</strong></div><ProgressBar value={Math.max(0, Math.min(100, Number.parseFloat(club.share)))} tone="rank" /></div><div className="rank-share"><span>Paid by the contract</span><strong>{formatToken(club.distributedAmount)}</strong></div><div className="rank-share"><span>Next settlement</span><strong>{formatDate(club.nextDistributionAt)}</strong></div></motion.article>)}</div>
        {clubs.length === 0 ? <p className="empty-copy">The Leaders Club contract has not reported a schedule or a settlement yet, so there is nothing on-chain to show.</p> : null}
      </section>
      <div className="integrity-note"><Users size={17} /><span>Club membership is the contract&apos;s ClubEntered record and every payout a ClubPaid event; no operator can change either.</span></div>
    </>
  );
}
